import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService';
import {
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    TextField,
    Typography,
    withStyles
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Moment from 'moment';
import { Select } from '@material-ui/core';
import { DatePicker, MuiPickersUtilsProvider } from 'material-ui-pickers';
import SimpleCountriesInput from '../../../form/SimpleCountriesInput';
import Customer from './customer';
import { listCustomers } from '../../../api/CustomerApi';
import PostCodeApi from '../../../api/PostCodeApi';
import PropTypes from 'prop-types';
import { modalStyles } from './styles';
import UploadButton from '../UploadButton';
import PhoneNumberInput from 'material-ui-phone-number';
import {
    DEFAULT_PHOTO,
    PREFERRED_PHONE_COUNTRIES,
    CUSTOMER_DEFAULT_FIELDS,
    LEADS_DEFAULT_FIELDS,
    HEIGHT_OPTIONS,
    HEIGHT_DEFAULT,
    WEIGHT_OPTIONS,
    WEIGHT_DEFAULT,
    DEFAULT_ETHNICITY_OPTIONS,
    DEFAULT_STATUS_OPTIONS,
    MARKETING_DATA_FIELD_NAMES
} from '../../../../collums-constants';
import DeleteIcon from '@material-ui/icons/DeleteOutline';
import { isValidPhone } from '../../../helpers/validPhoneNumber';
import { getOrganisationCountryCode } from '../../../helpers';
import OrganisationApi from '../../../api/organizationApi';
import { getDateNoTimezone } from '../../../helpers/timezone';
import ListApi from '../../../api/ListApi';

const CUSTOMER_SEARCH_ROWS = 10;

// Listbox for the "Referred by" autocomplete with a "Load more" button below the options.
const CustomerListbox = React.forwardRef(function CustomerListbox({ hasMore, isLoading, onLoadMore, ...props }, ref) {
    return (
        <ul ref={ref} {...props}>
            {props.children}
            {hasMore && (
                <li style={{ display: 'flex', justifyContent: 'center', padding: 8 }}>
                    <Button
                        style={{ textTransform: 'none' }}
                        disabled={isLoading}
                        // Keep focus on the input, otherwise the popup closes before the click.
                        onMouseDown={e => e.preventDefault()}
                        onClick={onLoadMore}
                    >
                        {isLoading ? 'Loading...' : 'Load more'}
                    </Button>
                </li>
            )}
        </ul>
    );
});

CustomerListbox.propTypes = {
    children: PropTypes.node,
    hasMore: PropTypes.bool,
    isLoading: PropTypes.bool,
    onLoadMore: PropTypes.func
};

function CustomerForm({
    classes,
    //eslint-disable-next-line
    inputNode,
    formData,
    DateFnsUtils,
    enLocale,
    TITLES,
    GENDERS,
    PHONE_TYPE_OPTIONS,
    REFERRAL_SOURCES_TYPE,
    customer,
    setFile,
    setDeletedPhoto,
    addAsLead = false,
    onFieldBlur
}) {
    const [form, setForm] = useState(Customer(customer ? customer : {}));
    const [customers, setCustomers] = useState([]);
    const lastCustomerSearch = useRef(0);
    const activeCustomerSearch = useRef('');
    const defaultCustomers = useRef([]);
    const [customerSearch, setCustomerSearch] = useState({ value: '', page: 0, hasMore: false, isLoading: false });
    const [isReferredByFriend, setIsReferredByFriend] = useState(
        customer?.referralSource === REFERRAL_SOURCES_TYPE.FRIEND
    );
    const [zipError, setZipError] = useState(null);
    const [avatar, setAvatar] = useState(form.avatar || DEFAULT_PHOTO.DEFAULT_MALE);
    const [changedPhoto, setChangedPhoto] = useState(false);
    const [referralSourceType, setReferralSourceType] = useState(Object.values(REFERRAL_SOURCES_TYPE).sort());
    const [isInvalid, setIsInvalid] = useState({
        otherPhone: false,
        phone: false
    });

    const [countryCode, setCountryCode] = useState(null);
    const [orgCustomerFields, setOrgCustomerFields] = useState(CUSTOMER_DEFAULT_FIELDS);
    const [orgLeadsFields, setOrgLeadsFields] = useState(LEADS_DEFAULT_FIELDS);
    const [ethnicityOptions, setEthnicityOptions] = useState(DEFAULT_ETHNICITY_OPTIONS);
    const [patientStatusOptions, setPatientStatusOptions] = useState(DEFAULT_STATUS_OPTIONS);
    const [marketingDataEnabled, setMarketingDataEnabled] = useState(false);
    const [orgCancellationRules, setOrgCancellationRules] = useState('allow');
    const [orgReschedulingRule, setOrgReschedulingRule] = useState('allow');

    const { placePredictions, getPlacePredictions, placesService, isPlacePredictionsLoading } = usePlacesService({
        apiKey: process.env.REACT_APP_GOOGLE_API_KEY,
        debounce: 500,
        options: {
            componentRestrictions: { country: 'UK' }
        }
    });

    const [postcodeMap, setPostcodeMap] = useState({});

    useEffect(() => {
        if (!placePredictions?.length || !placesService) return;
        placePredictions.forEach(prediction => {
            const placeId = prediction.place_id;
            if (!placeId || postcodeMap[placeId] !== undefined) return;
            setPostcodeMap(prev => ({ ...prev, [placeId]: null }));
            placesService.getDetails({ placeId, fields: ['address_component'] }, details => {
                const comp = details?.address_components?.find(c => c.types?.includes('postal_code'));
                setPostcodeMap(prev => ({ ...prev, [placeId]: comp?.long_name || '' }));
            });
        });
    }, [placePredictions, placesService]); // eslint-disable-line react-hooks/exhaustive-deps

    const getAddressComponent = useCallback((place, code) => {
        const components = place?.address_components || [];
        const component = components.find(c => {
            const types = c?.types || [];
            if (code === 'county')
                return types.includes('administrative_area_level_2') || types.includes('administrative_area_level_1');
            if (code === 'town') return types.includes('locality') || types.includes('postal_town');
            return types.includes(code);
        });
        return component?.long_name || component?.short_name || '';
    }, []);

    const handlePlaceSelect = useCallback(
        place => {
            if (!place?.place_id || !placesService) {
                setForm(prev => ({
                    ...prev,
                    address1: place?.structured_formatting?.main_text || place?.description || ''
                }));
                return;
            }
            placesService.getDetails({ placeId: place.place_id, language: 'en' }, details => {
                const streetNumber = getAddressComponent(details, 'street_number');
                const route = getAddressComponent(details, 'route');
                const streetAddress = [streetNumber, route].filter(Boolean).join(' ');
                setForm(prev => ({
                    ...prev,
                    address1: streetAddress || place?.structured_formatting?.main_text || place?.description || '',
                    city: getAddressComponent(details, 'town') || prev.city,
                    county: getAddressComponent(details, 'county') || prev.county,
                    country: {
                        label: getAddressComponent(details, 'country') || 'United Kingdom',
                        value: 'GB'
                    },
                    postal: getAddressComponent(details, 'postal_code') || prev.postal
                }));
            });
        },
        [placesService, getAddressComponent]
    );

    const mergeWithDefaults = (savedFields, defaultFields) => {
        const defaultNames = new Set(defaultFields.map(f => f.name));
        const filtered = savedFields.filter(f => defaultNames.has(f.name));
        const savedNames = new Set(savedFields.map(f => f.name));
        const missingFields = defaultFields.filter(f => !savedNames.has(f.name));
        return [...filtered, ...missingFields];
    };

    const effectiveCustomerFields = useMemo(() => {
        let fields;
        if (addAsLead) fields = orgLeadsFields;
        else if (customer) fields = orgCustomerFields.map(f => ({ ...f, show: true }));
        else fields = orgCustomerFields;
        if (!marketingDataEnabled) {
            fields = fields.filter(f => !MARKETING_DATA_FIELD_NAMES.includes(f.name));
        }
        return fields;
    }, [customer, addAsLead, orgCustomerFields, orgLeadsFields, marketingDataEnabled]);

    const { leftColumnFields, rightColumnFields } = useMemo(() => {
        const visibleFields = effectiveCustomerFields
            .map((field, originalIndex) => ({ ...field, tabIndex: (originalIndex + 1) * 10 }))
            .filter(f => f.show);
        return {
            leftColumnFields: visibleFields.filter((_, i) => i % 2 === 0),
            rightColumnFields: visibleFields.filter((_, i) => i % 2 === 1)
        };
    }, [effectiveCustomerFields]);

    useEffect(() => {
        ListApi.getAll().then(lists => {
            const referralSource = lists.find(el => el.sys_name === 'client_referral_sources');
            if (referralSource) {
                const options = referralSource.values.split('\n');
                if (!options.includes('Friend')) {
                    options.push('Friend');
                }
                if (!options.includes('Email')) {
                    options.push('Email');
                }
                if (!options.includes('Telephone')) {
                    options.push('Telephone');
                }
                setReferralSourceType(options);
            }
            const ethnicity = lists.find(el => el.sys_name === 'client_ethnicity');
            if (ethnicity) {
                setEthnicityOptions(ethnicity.values.split('\n').filter(v => v.trim()));
            }
            const status = lists.find(el => el.sys_name === 'client_status');
            if (status) {
                setPatientStatusOptions(status.values.split('\n').filter(v => v.trim()));
            }
        });
        //eslint-disable-next-line
    }, []);

    useEffect(() => {
        formData.current = form;
        //eslint-disable-next-line
    }, [form]);

    useEffect(() => {
        listCustomers(null, null, null, null, null, true).then(res => {
            defaultCustomers.current = res.customers;
            // Don't overwrite results of a search started before the default list arrived.
            if (!activeCustomerSearch.current) setCustomers(res.customers);
        });
    }, []);

    useEffect(() => {
        const getOrganisation = async () => {
            return await OrganisationApi.getOrg();
        };

        if (!countryCode) {
            getOrganisation().then(org => {
                setCountryCode(getOrganisationCountryCode(org));
                setMarketingDataEnabled(!!org.marketingDataEnabled);
                if (org.cancellationRules) setOrgCancellationRules(org.cancellationRules);
                if (org.reschedulingRule) setOrgReschedulingRule(org.reschedulingRule);
                if (org.customerFields && org.customerFields.length > 0) {
                    setOrgCustomerFields(mergeWithDefaults(org.customerFields, CUSTOMER_DEFAULT_FIELDS));
                }
                if (org.leadsFields && org.leadsFields.length > 0) {
                    setOrgLeadsFields(mergeWithDefaults(org.leadsFields, LEADS_DEFAULT_FIELDS));
                }
            });
        }
    }, [countryCode]);

    const handleChange = event => {
        if (event) {
            if (event.value && event.label) {
                setForm({ ...form, country: event });
            } else {
                if (event.target.name === 'referralSource') {
                    // Used to dynamically enable/disable client selection if referred by a friend.
                    const isReferring = event.target.value === REFERRAL_SOURCES_TYPE.FRIEND;
                    setIsReferredByFriend(isReferring);
                    if (!isReferring && form.referredBy) {
                        setForm({ ...form, referredBy: '', [event.target.name]: event.target.value });
                        return;
                    }
                } else if (event.target.name === 'otherPhoneType') {
                    const otherPhone = form.otherPhone && form.otherPhone.length ? form.otherPhone : [{}];
                    otherPhone[0].type = event.target.value;
                    if (!otherPhone[0].type) otherPhone[0].phone = '';
                    setForm({ ...form, otherPhone });
                    return;
                }
                setForm({ ...form, [event.target.name]: event.target.value });
            }
        } else {
            setForm({ ...form, country: { label: '', value: '' } });
        }
    };

    const handleCheckboxChange = (event, value) => {
        setForm({ ...form, [event.target.name]: value });
    };

    const handlePhotoConsentChange = option => {
        const current = form.photoConsent || [];
        const newConsent = current.includes(option) ? current.filter(c => c !== option) : [...current, option];
        setForm({ ...form, photoConsent: newConsent });
    };

    const searchCustomers = async (value, page) => {
        // Ignore responses from outdated searches, they may resolve after the latest one.
        const searchId = ++lastCustomerSearch.current;
        activeCustomerSearch.current = value;
        setCustomerSearch(prev => ({ ...prev, isLoading: true }));
        try {
            const res = await listCustomers(value, CUSTOMER_SEARCH_ROWS, page, null, null, true);
            if (searchId !== lastCustomerSearch.current) return;
            setCustomers(prev => (page === 0 ? res.customers : [...prev, ...res.customers]));
            setCustomerSearch({
                value,
                page,
                hasMore: res.customers.length === CUSTOMER_SEARCH_ROWS,
                isLoading: false
            });
        } catch (e) {
            // Keep the current results, the user can retry by typing or clicking "Load more".
        } finally {
            if (searchId === lastCustomerSearch.current) {
                setCustomerSearch(prev => ({ ...prev, isLoading: false }));
            }
        }
    };

    const resetCustomerSearch = () => {
        // Invalidate pending searches and go back to the default list.
        lastCustomerSearch.current++;
        activeCustomerSearch.current = '';
        setCustomers(defaultCustomers.current);
        setCustomerSearch({ value: '', page: 0, hasMore: false, isLoading: false });
    };

    const handleInputChange = async (event, value, reason) => {
        const filterByNameLenght = 3;

        // Skip 'reset' (input set to the selected option label) so selecting doesn't trigger a search.
        if (reason !== 'input' && reason !== 'clear') return;

        if (value.length >= filterByNameLenght) {
            await searchCustomers(value, 0);
        } else if (activeCustomerSearch.current) {
            resetCustomerSearch();
        }
    };

    const handleLoadMoreCustomers = () => searchCustomers(customerSearch.value, customerSearch.page + 1);

    const handleAddressSearch = async () => {
        if (form.postal.length >= 5) {
            const response = await PostCodeApi.searchForAddress(form.postal);
            if (response.error) {
                setZipError(response.error);
                setForm({
                    ...form,
                    city: '',
                    county: '',
                    country: {
                        label: 'United Kingdom',
                        value: 'GB'
                    },
                    address1: '',
                    address2: ''
                });
            } else if (response.city !== form.city) {
                setZipError(response.error);
                setForm({
                    ...form,
                    city: response.city,
                    county: response.county || '',
                    country: {
                        label: response.country || 'United Kingdom',
                        value: 'GB'
                    },
                    address1: '',
                    address2: ''
                });
            }
        }
    };

    const onDropAvatar = async (file, picture) => {
        setChangedPhoto(true);
        if (customer && customer.id) {
            setDeletedPhoto(false);
        }
        setAvatar(picture);
        if (setFile) {
            setFile(file);
        }
    };

    useEffect(() => {
        if (!form.avatar && !changedPhoto) {
            if (form.gender === GENDERS.FEMALE) {
                setAvatar(DEFAULT_PHOTO.DEFAULT_FEMALE);
            } else {
                setAvatar(DEFAULT_PHOTO.DEFAULT_MALE);
            }
        }
        // eslint-disable-next-line
    }, [form.avatar]);

    const removePhoto = () => {
        if (setFile) {
            if (form.gender === GENDERS.FEMALE) {
                setAvatar(DEFAULT_PHOTO.DEFAULT_FEMALE);
            } else {
                setAvatar(DEFAULT_PHOTO.DEFAULT_MALE);
            }
            if (customer && customer.id) {
                setDeletedPhoto(true);
            }
            setFile(null);
        }
    };

    const handleChangePhoneNumber = (newValue, isOtherPhone) => {
        if (isOtherPhone) {
            const otherPhone = form.otherPhone && form.otherPhone.length ? form.otherPhone : [{}];
            otherPhone[0].phone = newValue;
            setForm({ ...form, otherPhone });
            return;
        }
        setForm({
            ...form,
            mobilePhone: newValue
        });
    };

    const renderField = fieldConfig => {
        const { name, required, tabIndex } = fieldConfig;

        switch (name) {
            case 'title':
                return (
                    <FormControl key={name} style={{ marginLeft: 8, minHeight: 38 }}>
                        <Autocomplete
                            ListboxProps={{ id: 'listbox' }}
                            options={Object.values(TITLES).sort()}
                            autoHighlight
                            value={form.title}
                            onChange={(e, value) => handleChange({ target: { name: 'title', value } })}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    inputProps={{ ...params.inputProps, tabIndex }}
                                    margin="dense"
                                    label="Title"
                                    required={required}
                                    className={`${classes.autocomplete} ${classes.font}`}
                                    variant="outlined"
                                />
                            )}
                        />
                    </FormControl>
                );

            case 'firstName':
                return (
                    <TextField
                        key={name}
                        variant={'outlined'}
                        required={required}
                        id="first-name"
                        label="First Name"
                        name="firstName"
                        value={form.firstName}
                        onChange={handleChange}
                        className={classes.font}
                        margin="normal"
                        inputProps={{ tabIndex }}
                    />
                );

            case 'surname':
                return (
                    <TextField
                        key={name}
                        variant={'outlined'}
                        required={required}
                        id="surname"
                        label="Surname"
                        name="surname"
                        value={form.surname}
                        onChange={handleChange}
                        className={classes.font}
                        margin="normal"
                        inputProps={{ tabIndex }}
                    />
                );

            case 'middleName':
                return (
                    <TextField
                        key={name}
                        variant={'outlined'}
                        id="middle-name"
                        label="Middle Name"
                        name="middleName"
                        required={required}
                        value={form.middleName}
                        onChange={handleChange}
                        className={classes.font}
                        margin="normal"
                        inputProps={{ tabIndex }}
                    />
                );

            case 'prevSurname':
                return (
                    <TextField
                        key={name}
                        variant={'outlined'}
                        id="prev-surname"
                        label="Prev Surname"
                        name="prevSurname"
                        required={required}
                        value={form.prevSurname}
                        onChange={handleChange}
                        className={classes.font}
                        margin="normal"
                        inputProps={{ tabIndex }}
                    />
                );

            case 'aka':
                return (
                    <TextField
                        key={name}
                        variant={'outlined'}
                        id="aka"
                        label="Aka"
                        name="aka"
                        required={required}
                        value={form.aka}
                        onChange={handleChange}
                        className={classes.font}
                        margin="normal"
                        inputProps={{ tabIndex }}
                    />
                );

            case 'email':
                return (
                    <TextField
                        key={name}
                        variant="outlined"
                        required={required}
                        label="E-mail"
                        name="email"
                        className={classes.font}
                        value={form.email}
                        onChange={handleChange}
                        onBlur={() => onFieldBlur && onFieldBlur('email', form.email)}
                        emailMask
                        inputProps={{ tabIndex }}
                    />
                );

            case 'mobilePhone':
                return (
                    <PhoneNumberInput
                        key={name}
                        variant="outlined"
                        value={form.mobilePhone}
                        label="Mobile Phone"
                        enableLongNumbers={true}
                        required={required}
                        error={isInvalid.phone}
                        inputClass={classes.font}
                        onChange={(value, phoneInfo) => {
                            setIsInvalid({
                                ...isInvalid,
                                phone: !isValidPhone(value, phoneInfo.countryCode, phoneInfo.dialCode)
                            });
                            handleChangePhoneNumber(value);
                        }}
                        onBlur={() => onFieldBlur && onFieldBlur('phone', form.mobilePhone)}
                        preferredCountries={PREFERRED_PHONE_COUNTRIES}
                        defaultCountry={!form?.mobilePhone ? countryCode : null}
                        tabIndex={tabIndex}
                    />
                );

            case 'otherPhoneType':
                return (
                    <React.Fragment key={name}>
                        <FormControl variant={'outlined'} style={{ width: '100%', alignSelf: 'center' }}>
                            <InputLabel className={classes.font} required={required}>
                                Other Phone
                            </InputLabel>
                            <Select
                                id="other-phone-type"
                                name="otherPhoneType"
                                required={required}
                                value={
                                    (form.otherPhone && form.otherPhone[0] && form.otherPhone[0].type) ||
                                    form.otherPhoneType
                                }
                                onChange={handleChange}
                                className={`${classes.font} ${classes.select}`}
                                inputProps={{ tabIndex }}
                            >
                                <MenuItem value={''}>None</MenuItem>
                                {Object.keys(PHONE_TYPE_OPTIONS).map(opt => {
                                    return (
                                        <MenuItem key={opt} value={PHONE_TYPE_OPTIONS[opt]}>
                                            {PHONE_TYPE_OPTIONS[opt]}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                        {((form.otherPhone && form.otherPhone[0] && form.otherPhone[0].type) ||
                            form.otherPhoneType) && (
                            <PhoneNumberInput
                                variant="outlined"
                                value={(form.otherPhone[0] && form.otherPhone[0].phone) || form.otherPhoneNumber}
                                label="Phone"
                                required={required}
                                error={isInvalid.otherPhone}
                                inputClass={classes.font}
                                onChange={(value, phoneInfo) => {
                                    setIsInvalid({
                                        ...isInvalid,
                                        otherPhone: !isValidPhone(value, phoneInfo.countryCode, phoneInfo.dialCode)
                                    });
                                    handleChangePhoneNumber(value, true);
                                }}
                                preferredCountries={PREFERRED_PHONE_COUNTRIES}
                                defaultCountry={
                                    (form.otherPhone[0] && form.otherPhone[0].phone) || form.otherPhoneNumber
                                        ? null
                                        : countryCode
                                }
                                tabIndex={tabIndex + 1}
                            />
                        )}
                    </React.Fragment>
                );

            case 'postal':
                return (
                    <TextField
                        key={name}
                        variant={'outlined'}
                        helperText={zipError}
                        id="postal"
                        label="Postcode"
                        name="postal"
                        required={required}
                        className={classes.font}
                        value={form.postal}
                        onKeyPress={e => {
                            if (e.key === 'Enter') handleAddressSearch();
                        }}
                        onChange={handleChange}
                        margin="normal"
                        inputProps={{ tabIndex }}
                    />
                );

            case 'address1':
                return (
                    <Autocomplete
                        key={name}
                        id="address-google-autocomplete"
                        className={classes.addressAutocomplete}
                        style={{ width: '100%' }}
                        options={placePredictions || []}
                        getOptionLabel={option => (option.description || '').replace(/, UK$/, '')}
                        inputValue={form.address1}
                        onInputChange={(e, value, reason) => {
                            if (reason === 'input') {
                                setForm(prev => ({ ...prev, address1: value }));
                                getPlacePredictions({ input: value });
                            }
                        }}
                        onChange={(e, value) => handlePlaceSelect(value)}
                        filterOptions={x => x}
                        loading={isPlacePredictionsLoading}
                        loadingText="Loading..."
                        noOptionsText="Start typing your address..."
                        freeSolo
                        disableClearable
                        renderOption={option => {
                            const desc = (option.description || '').replace(/, UK$/, '');
                            const postcode = postcodeMap[option.place_id];
                            return postcode ? `${desc}, ${postcode}` : desc;
                        }}
                        renderInput={params => (
                            <TextField
                                {...params}
                                variant="outlined"
                                label="Address line 1"
                                name="address1"
                                placeholder="Start typing your address"
                                required={required}
                                className={classes.font}
                                margin="none"
                                inputProps={{
                                    ...params.inputProps,
                                    tabIndex,
                                    value: form.address1,
                                    style: { padding: '18.5px 14px', height: '38px', boxSizing: 'border-box' }
                                }}
                                InputProps={{ ...params.InputProps, style: { padding: 0 } }}
                            />
                        )}
                    />
                );

            case 'address2':
                return (
                    <TextField
                        key={name}
                        variant={'outlined'}
                        id="address-2"
                        label="Address line 2"
                        name="address2"
                        required={required}
                        className={classes.font}
                        value={form.address2}
                        onChange={handleChange}
                        margin="normal"
                        inputProps={{ tabIndex }}
                    />
                );

            case 'city':
            case 'City':
                return (
                    <TextField
                        key={name}
                        variant={'outlined'}
                        id="city"
                        label="City"
                        name="city"
                        required={required}
                        value={form.city}
                        className={classes.font}
                        onChange={handleChange}
                        margin="normal"
                        inputProps={{ tabIndex }}
                    />
                );

            case 'county':
                return (
                    <TextField
                        key={name}
                        variant={'outlined'}
                        id="county"
                        label="County"
                        name="county"
                        required={required}
                        value={form.county}
                        className={classes.font}
                        onChange={handleChange}
                        margin="normal"
                        inputProps={{ tabIndex }}
                    />
                );

            case 'dateOfBirth':
                return (
                    <MuiPickersUtilsProvider key={name} utils={DateFnsUtils} locale={enLocale}>
                        <DatePicker
                            variant={'outlined'}
                            autoOk
                            className={`${classes.font} ${classes.hiddenCalendarIcon}`}
                            keyboard
                            name="dateOfBirth"
                            label="Date of Birth"
                            placeholder="dd/mm/yyyy"
                            format={'dd/MM/yyyy'}
                            required={required}
                            keyboardIcon={null}
                            mask={value => (value ? [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/] : [])}
                            value={form.dateOfBirth ? Moment.parseZone(form.dateOfBirth).toDate() : form.dateOfBirth}
                            onInputChange={value => {
                                if (value.target.value === '')
                                    handleChange({
                                        target: { name: 'dateOfBirth', value: null }
                                    });
                            }}
                            onChange={value => {
                                if (!value) return;

                                const date = Moment.utc(getDateNoTimezone(value));

                                handleChange({
                                    target: { name: 'dateOfBirth', value: date }
                                });
                            }}
                            disableOpenOnEnter
                            animateYearScrolling={false}
                            inputRef={ref => {
                                if (ref) ref.tabIndex = tabIndex;
                            }}
                        />
                    </MuiPickersUtilsProvider>
                );

            case 'gender':
                return (
                    <FormControl key={name} style={{ marginLeft: 8 }}>
                        <Autocomplete
                            ListboxProps={{ id: 'listbox' }}
                            options={Object.values(GENDERS).sort()}
                            autoHighlight
                            value={form.gender}
                            onChange={(e, value) => handleChange({ target: { name: 'gender', value } })}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    inputProps={{ ...params.inputProps, tabIndex }}
                                    margin="dense"
                                    label="Gender"
                                    required={required}
                                    className={`${classes.autocomplete} ${classes.font}`}
                                    variant="outlined"
                                />
                            )}
                        />
                    </FormControl>
                );

            case 'country':
                return (
                    <SimpleCountriesInput
                        key={name}
                        name="country"
                        value={form.country}
                        onChange={handleChange}
                        required={required}
                        className={`${classes.font} ${classes.autocomplete}`}
                        tabIndex={tabIndex}
                    />
                );

            case 'referralSource':
                return (
                    <FormControl key={name} style={{ marginLeft: 8 }}>
                        <Autocomplete
                            ListboxProps={{ id: 'listbox' }}
                            getOptionLabel={option => option && option}
                            options={referralSourceType}
                            autoHighlight
                            value={form.referralSource}
                            onChange={(e, value) => handleChange({ target: { name: 'referralSource', value } })}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    inputProps={{ ...params.inputProps, tabIndex }}
                                    margin="dense"
                                    label="Referral Source"
                                    variant="outlined"
                                    required={required}
                                    className={`${classes.autocomplete} ${classes.font}`}
                                />
                            )}
                        />
                    </FormControl>
                );

            case 'referredBy':
                return (
                    <FormControl key={name} style={{ marginLeft: 8 }} variant={'outlined'}>
                        <Autocomplete
                            disabled={!isReferredByFriend}
                            ListboxComponent={CustomerListbox}
                            ListboxProps={{
                                id: 'listbox',
                                hasMore: customerSearch.hasMore,
                                isLoading: customerSearch.isLoading,
                                onLoadMore: handleLoadMoreCustomers
                            }}
                            getOptionLabel={option =>
                                option
                                    ? [
                                          option.firstName,
                                          option.middleName,
                                          option.surname,
                                          option.dateOfBirth && Moment(option.dateOfBirth).format('DD/MM/YYYY')
                                      ]
                                          .filter(Boolean)
                                          .join(' ')
                                    : ''
                            }
                            filterOptions={x => x}
                            options={customers}
                            autoHighlight
                            value={form.referredBy}
                            onChange={(e, value) => handleChange({ target: { name: 'referredBy', value } })}
                            onInputChange={handleInputChange}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    inputProps={{ ...params.inputProps, tabIndex }}
                                    className={`${classes.autocomplete} ${classes.font}`}
                                    margin="dense"
                                    label="Referred by"
                                    variant="outlined"
                                    required={required}
                                />
                            )}
                        />
                    </FormControl>
                );

            case 'ethnicity':
                return (
                    <FormControl key={name} style={{ marginLeft: 8 }}>
                        <Autocomplete
                            ListboxProps={{ id: 'listbox' }}
                            options={ethnicityOptions}
                            autoHighlight
                            freeSolo
                            value={form.ethnicity || ''}
                            onChange={(e, value) => handleChange({ target: { name: 'ethnicity', value } })}
                            onInputChange={(e, value) => handleChange({ target: { name: 'ethnicity', value } })}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    inputProps={{ ...params.inputProps, tabIndex }}
                                    margin="dense"
                                    label="Ethnicity"
                                    required={required}
                                    className={`${classes.autocomplete} ${classes.font}`}
                                    variant="outlined"
                                />
                            )}
                        />
                    </FormControl>
                );

            case 'patientStatus':
                return (
                    <FormControl key={name} style={{ marginLeft: 8 }}>
                        <Autocomplete
                            ListboxProps={{ id: 'listbox' }}
                            options={patientStatusOptions}
                            autoHighlight
                            freeSolo
                            value={form.patientStatus || ''}
                            onChange={(e, value) => handleChange({ target: { name: 'patientStatus', value } })}
                            onInputChange={(e, value) => handleChange({ target: { name: 'patientStatus', value } })}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    inputProps={{ ...params.inputProps, tabIndex }}
                                    margin="dense"
                                    label="Patient Status"
                                    required={required}
                                    className={`${classes.autocomplete} ${classes.font}`}
                                    variant="outlined"
                                />
                            )}
                        />
                    </FormControl>
                );

            case 'passportId':
                return (
                    <TextField
                        key={name}
                        variant={'outlined'}
                        id="passport-id"
                        label="Passport/ID"
                        name="passportId"
                        required={required}
                        value={form.passportId}
                        onChange={handleChange}
                        className={classes.font}
                        margin="normal"
                        inputProps={{ tabIndex }}
                    />
                );

            case 'nhsNumber':
                return (
                    <TextField
                        key={name}
                        variant={'outlined'}
                        id="nhs-number"
                        label="NHS no"
                        name="nhsNumber"
                        required={required}
                        value={form.nhsNumber}
                        onChange={handleChange}
                        className={classes.font}
                        margin="normal"
                        inputProps={{ tabIndex }}
                    />
                );

            case 'occupation':
                return (
                    <TextField
                        key={name}
                        variant={'outlined'}
                        id="occupation"
                        label="Occupation"
                        name="occupation"
                        required={required}
                        value={form.occupation}
                        onChange={handleChange}
                        className={classes.font}
                        margin="normal"
                        inputProps={{ tabIndex }}
                    />
                );

            case 'height':
                return (
                    <FormControl key={name} style={{ marginLeft: 8 }}>
                        <Autocomplete
                            ListboxProps={{ id: 'height-listbox' }}
                            options={HEIGHT_OPTIONS}
                            getOptionLabel={option => option.label || ''}
                            autoHighlight
                            value={HEIGHT_OPTIONS.find(h => h.value === form.height) || null}
                            onOpen={() => {
                                if (!form.height) {
                                    setTimeout(() => {
                                        const listbox = document.getElementById('height-listbox');
                                        if (listbox) {
                                            const idx = HEIGHT_OPTIONS.findIndex(h => h.value === HEIGHT_DEFAULT);
                                            if (idx > 0) listbox.scrollTop = idx * 36;
                                        }
                                    }, 0);
                                }
                            }}
                            onChange={(e, value) =>
                                handleChange({ target: { name: 'height', value: value ? value.value : null } })
                            }
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    inputProps={{ ...params.inputProps, tabIndex }}
                                    margin="dense"
                                    label="Height"
                                    required={required}
                                    className={`${classes.autocomplete} ${classes.font}`}
                                    variant="outlined"
                                />
                            )}
                        />
                    </FormControl>
                );

            case 'weight':
                return (
                    <FormControl key={name} style={{ marginLeft: 8 }}>
                        <Autocomplete
                            freeSolo
                            ListboxProps={{ id: 'weight-listbox' }}
                            options={WEIGHT_OPTIONS}
                            getOptionLabel={option => {
                                if (typeof option === 'string') return option;
                                return option.label || '';
                            }}
                            autoHighlight
                            value={
                                WEIGHT_OPTIONS.find(w => w.value === form.weight) ||
                                (form.weight != null ? `${form.weight}` : null)
                            }
                            onOpen={() => {
                                if (!form.weight) {
                                    setTimeout(() => {
                                        const listbox = document.getElementById('weight-listbox');
                                        if (listbox) {
                                            const idx = WEIGHT_OPTIONS.findIndex(w => w.value === WEIGHT_DEFAULT);
                                            if (idx > 0) listbox.scrollTop = idx * 36;
                                        }
                                    }, 0);
                                }
                            }}
                            onChange={(e, value) => {
                                if (!value) {
                                    handleChange({ target: { name: 'weight', value: null } });
                                } else if (typeof value === 'object') {
                                    handleChange({ target: { name: 'weight', value: value.value } });
                                } else {
                                    const parsed = parseFloat(value);
                                    if (!isNaN(parsed) && parsed > 0) {
                                        handleChange({
                                            target: { name: 'weight', value: Math.round(parsed * 10) / 10 }
                                        });
                                    }
                                }
                            }}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    inputProps={{ ...params.inputProps, tabIndex }}
                                    margin="dense"
                                    label="Weight (kg)"
                                    required={required}
                                    className={`${classes.autocomplete} ${classes.font}`}
                                    variant="outlined"
                                    onBlur={e => {
                                        const val = e.target.value;
                                        const numMatch = val && val.match(/^[\d.]+/);
                                        if (numMatch) {
                                            const parsed = parseFloat(numMatch[0]);
                                            if (!isNaN(parsed) && parsed > 0) {
                                                handleChange({
                                                    target: { name: 'weight', value: Math.round(parsed * 10) / 10 }
                                                });
                                            }
                                        }
                                    }}
                                />
                            )}
                        />
                    </FormControl>
                );

            case 'campaignTerm':
                return (
                    <TextField
                        key={name}
                        variant={'outlined'}
                        id="campaign-term"
                        label="Campaign Term"
                        name="campaignTerm"
                        required={required}
                        value={form.campaignTerm}
                        onChange={handleChange}
                        className={classes.font}
                        margin="normal"
                        inputProps={{ tabIndex }}
                    />
                );

            case 'campaignContent':
                return (
                    <TextField
                        key={name}
                        variant={'outlined'}
                        id="campaign-content"
                        label="Campaign Content"
                        name="campaignContent"
                        required={required}
                        value={form.campaignContent}
                        onChange={handleChange}
                        className={classes.font}
                        margin="normal"
                        inputProps={{ tabIndex }}
                    />
                );

            case 'campaignName':
                return (
                    <TextField
                        key={name}
                        variant={'outlined'}
                        id="campaign-name"
                        label="Campaign Name"
                        name="campaignName"
                        required={required}
                        value={form.campaignName}
                        onChange={handleChange}
                        className={classes.font}
                        margin="normal"
                        inputProps={{ tabIndex }}
                    />
                );

            case 'campaignSource':
                return (
                    <TextField
                        key={name}
                        variant={'outlined'}
                        id="campaign-source"
                        label="Campaign Source"
                        name="campaignSource"
                        required={required}
                        value={form.campaignSource}
                        onChange={handleChange}
                        className={classes.font}
                        margin="normal"
                        inputProps={{ tabIndex }}
                    />
                );

            case 'campaignMedium':
                return (
                    <TextField
                        key={name}
                        variant={'outlined'}
                        id="campaign-medium"
                        label="Campaign Medium"
                        name="campaignMedium"
                        required={required}
                        value={form.campaignMedium}
                        onChange={handleChange}
                        className={classes.font}
                        margin="normal"
                        inputProps={{ tabIndex }}
                    />
                );

            case 'firstPageVisited':
                return (
                    <TextField
                        key={name}
                        variant={'outlined'}
                        id="first-page-visited"
                        label="First Page Visited"
                        name="firstPageVisited"
                        required={required}
                        value={form.firstPageVisited}
                        onChange={handleChange}
                        className={classes.font}
                        margin="normal"
                        inputProps={{ tabIndex }}
                    />
                );

            case 'gclid':
                return (
                    <TextField
                        key={name}
                        variant={'outlined'}
                        id="gclid"
                        label="GCLID"
                        name="gclid"
                        required={required}
                        value={form.gclid}
                        onChange={handleChange}
                        className={classes.font}
                        margin="normal"
                        inputProps={{ tabIndex }}
                    />
                );

            case 'pageEnquiredThrough':
                return (
                    <TextField
                        key={name}
                        variant={'outlined'}
                        id="page-enquired-through"
                        label="Page Enquired Through"
                        name="pageEnquiredThrough"
                        required={required}
                        value={form.pageEnquiredThrough}
                        onChange={handleChange}
                        className={classes.font}
                        margin="normal"
                        inputProps={{ tabIndex }}
                    />
                );

            default:
                return null;
        }
    };

    return (
        <form ref={node => (inputNode = node)} id="create-customer-modal">
            <Grid container alignItems="stretch" direction="row">
                {/* Avatar section */}
                <div style={{ display: 'flex', padding: '10px', flexDirection: 'column' }}>
                    <img alt="Customer avatar" style={{ height: '128px', width: '128px' }} src={avatar}></img>
                    <div style={{ display: 'flex' }}>
                        <UploadButton
                            buttonText="Choose image"
                            onChange={onDropAvatar}
                            imgExtension={['.jpg', '.gif', '.png', '.gif']}
                            maxFileSize={5242880}
                            style={{ width: '128px', marginTop: '20px' }}
                        />
                        <IconButton
                            aria-label="upload picture"
                            component="span"
                            onClick={removePhoto}
                            style={{ marginTop: 15 }}
                        >
                            <DeleteIcon color="error" />
                        </IconButton>
                    </div>
                </div>

                {/* Column 1 - fields at even indices */}
                <Grid item>
                    <div className={classes.formContainer}>{leftColumnFields.map(field => renderField(field))}</div>
                </Grid>

                {/* Column 2 - fields at odd indices */}
                <Grid item>
                    <div className={classes.formContainer}>{rightColumnFields.map(field => renderField(field))}</div>
                </Grid>

                {/* Checkboxes section */}
                <Grid item>
                    <div
                        className={classes.formContainer}
                        style={{ flexDirection: 'row', width: 'auto', marginRight: 10, flexWrap: 'wrap' }}
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', marginRight: 40 }}>
                            <Typography variant="subtitle2" style={{ marginTop: 8, marginBottom: 4, fontSize: 14 }}>
                                Marketing preference
                            </Typography>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        defaultChecked={form.receiveMarketingEmails}
                                        onChange={e => handleCheckboxChange(e, !form.receiveMarketingEmails)}
                                        name="receiveMarketingEmails"
                                        color="primary"
                                    />
                                }
                                className={`${classes.label}`}
                                label="Marketing email"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        defaultChecked={form.receiveMarketingSms}
                                        onChange={e => handleCheckboxChange(e, !form.receiveMarketingSms)}
                                        name="receiveMarketingSms"
                                        color="primary"
                                    />
                                }
                                className={`${classes.label}`}
                                label="Marketing SMS"
                            />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', marginRight: 40 }}>
                            <Typography variant="subtitle2" style={{ marginTop: 8, marginBottom: 4, fontSize: 14 }}>
                                Notifications
                            </Typography>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        defaultChecked={form.receiveNotificationEmails}
                                        onChange={e => handleCheckboxChange(e, !form.receiveNotificationEmails)}
                                        name="receiveNotificationEmails"
                                        color="primary"
                                    />
                                }
                                className={`${classes.label}`}
                                style={addAsLead ? { display: 'none' } : {}}
                                label="Notification email"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        defaultChecked={form.receiveNotificationSms}
                                        onChange={e => handleCheckboxChange(e, !form.receiveNotificationSms)}
                                        name="receiveNotificationSms"
                                        color="primary"
                                    />
                                }
                                className={`${classes.label}`}
                                style={addAsLead ? { display: 'none' } : {}}
                                label="Notification SMS"
                            />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', marginRight: 40 }}>
                            <Typography variant="subtitle2" style={{ marginTop: 8, marginBottom: 4, fontSize: 14 }}>
                                Photo consent
                            </Typography>
                            {['Marketing', 'Educational', 'In clinic'].map(option => (
                                <FormControlLabel
                                    key={option}
                                    control={
                                        <Checkbox
                                            checked={(form.photoConsent || []).includes(option)}
                                            onChange={() => handlePhotoConsentChange(option)}
                                            color="primary"
                                        />
                                    }
                                    className={`${classes.label}`}
                                    label={option}
                                />
                            ))}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="subtitle2" style={{ marginTop: 8, marginBottom: 4, fontSize: 14 }}>
                                Block online activity
                            </Typography>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={!!form.blockOnlineBooking}
                                        onChange={e => handleCheckboxChange(e, !form.blockOnlineBooking)}
                                        name="blockOnlineBooking"
                                        color="primary"
                                    />
                                }
                                className={`${classes.label}`}
                                label="Block online booking"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={
                                            orgCancellationRules === 'do-not-allow' || !!form.blockOnlineCancellation
                                        }
                                        onChange={e => handleCheckboxChange(e, !form.blockOnlineCancellation)}
                                        name="blockOnlineCancellation"
                                        color="primary"
                                        disabled={orgCancellationRules === 'do-not-allow'}
                                    />
                                }
                                className={`${classes.label}`}
                                label="Block online cancellation"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={
                                            orgReschedulingRule === 'do-not-allow' || !!form.blockOnlineRescheduling
                                        }
                                        onChange={e => handleCheckboxChange(e, !form.blockOnlineRescheduling)}
                                        name="blockOnlineRescheduling"
                                        color="primary"
                                        disabled={orgReschedulingRule === 'do-not-allow'}
                                    />
                                }
                                className={`${classes.label}`}
                                label="Block online rescheduling"
                            />
                        </div>
                    </div>
                </Grid>
            </Grid>
            {form.blockOnlineBooking && (
                <Grid container alignItems="stretch" direction="row">
                    <Grid item xs>
                        <TextField
                            id="blockOnlineText"
                            label="Text to show when blocked"
                            value={form.blockOnlineText || ''}
                            name="blockOnlineText"
                            multiline
                            rows="3"
                            className={`${classes.font} ${classes.noteField}`}
                            margin="normal"
                            onChange={handleChange}
                            variant="outlined"
                        />
                    </Grid>
                </Grid>
            )}
            <Grid container alignItems="stretch" direction="row">
                <Grid item xs>
                    <TextField
                        id="notes"
                        label={addAsLead ? 'Lead notes' : 'Client notes'}
                        value={form.notes}
                        name="notes"
                        multiline
                        rows="4"
                        className={`${classes.font} ${classes.noteField}`}
                        margin="normal"
                        onChange={handleChange}
                        variant="outlined"
                    />
                </Grid>
            </Grid>
        </form>
    );
}

CustomerForm.propTypes = {
    classes: PropTypes.object.isRequired,
    inputNode: PropTypes.object.isRequired,
    DateFnsUtils: PropTypes.any.isRequired,
    enLocale: PropTypes.any.isRequired,
    TITLES: PropTypes.array.isRequired,
    setFile: PropTypes.func,
    GENDERS: PropTypes.array.isRequired,
    PHONE_TYPE_OPTIONS: PropTypes.array.isRequired,
    formData: PropTypes.object.isRequired,
    customer: PropTypes.object,
    REFERRAL_SOURCES_TYPE: PropTypes.array.isRequired,
    setDeletedPhoto: PropTypes.func.isRequired,
    onFieldBlur: PropTypes.func,
    addAsLead: PropTypes.bool
};

export default withStyles(modalStyles)(CustomerForm);
